import React, {useCallback, useEffect, useRef} from 'react';
import './detailsAssistant.scss';
import {useSelectedDocContext, useStateLastSearchedQuery} from '../../search/search-hooks';
import {useTranslation} from 'react-i18next';
import {getCurrentLanguage} from '../../i18n-service';
import {
    extractSupportedTagsOnly,
    fetchSuggestions,
    getHibuAssistantRequestWithSearchRequest,
    getUuidForSingleDocument,
} from '../common/utils';
import {ChatWindow} from '../common/chatWindow';
import {useAiChatStream} from '../common/useAiChatStream';
import {ChatSummary} from '../common/chatSummary';
import {
    AiAssistantContextDocument,
    AiAssistantConversation,
    AiAssistantConversationContext,
    ChatType,
    DEFAULT_ASSISTANT_CONVERSATION_CONTEXT,
    Role,
} from '../common/types';
import {DefaultRootState, useDispatch, useSelector} from 'react-redux';
import {getSearchRequest} from '../../search/search.utils';
import {useStateConversation, useStateShowChatSuggestions} from '../duck/assistant-hooks';
import {
    doAddMessageRequest,
    doCreateConversationRequest,
    doDeleteConversationRequest,
    doLoadConversationRequest,
    doUpdateConversationContextRequest,
    doUpdateSuggestionsRequest,
} from '../duck/assistant-actions';
import {useAiAssistantSupportedTags} from '../../config/config-hooks';
import {showError} from '../../message-box/message-box-actions';
import {SearchPageType} from '../../search/search-config';
import {HibuEqualsFilter} from '../../facets/facet.types';
import {ChatInput} from '../common/chatInput';

export const DetailsAssistant = (): React.JSX.Element | null => {
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const supportedTags = useAiAssistantSupportedTags();
    const selectedDocument = useSelectedDocContext();
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const currentLanguage = getCurrentLanguage();
    const queryTerm = useStateLastSearchedQuery();
    const state: DefaultRootState = useSelector((state: DefaultRootState) => state);
    const activeSearchRequest = getSearchRequest(() => state, queryTerm, false, 0);

    const conversationId: string = selectedDocument
        ? getUuidForSingleDocument(selectedDocument.id)
        : getUuidForSingleDocument('someId');
    const conversation: AiAssistantConversation | undefined = useStateConversation(conversationId);
    const conversationContext: AiAssistantConversationContext =
        conversation?.context ?? DEFAULT_ASSISTANT_CONVERSATION_CONTEXT;
    const isLoadingConversations = useSelector((state: DefaultRootState) => state.assistant.isLoadingConversations);
    const showSuggestions = useStateShowChatSuggestions();

    const stream = useAiChatStream();
    const initialSuggestionsRequestedRef = useRef(false);

    useEffect(() => {
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    useEffect(() => {
        initialSuggestionsRequestedRef.current = false;
    }, [conversationId]);

    // Load conversation if it exists in db
    useEffect(() => {
        if (!conversation) {
            dispatch(doLoadConversationRequest(conversationId));
        }
    }, [conversationId, conversation, dispatch]);

    // Create a new conversation if it doesn't exist
    useEffect(() => {
        if (!conversation && !isLoadingConversations) {
            dispatch(
                doCreateConversationRequest(
                    ChatType.SINGLE,
                    conversationId,
                    t('search.details.hibu-assistant.welcome'),
                    Role.ASSISTANT,
                    activeSearchRequest,
                ),
            );
        }
    }, [activeSearchRequest, conversation, conversationId, dispatch, t, isLoadingConversations]);

    const fetchSuggestionsAndUpdate = useCallback(async () => {
        if (!selectedDocument || !conversation) {
            return;
        }

        const tags = extractSupportedTagsOnly(selectedDocument.hibuTags, supportedTags);
        const document = new AiAssistantContextDocument(selectedDocument.id, selectedDocument.title, tags);
        const fetchedSuggestions = await fetchSuggestions([document], currentLanguage, conversation, conversationContext);
        dispatch(doUpdateSuggestionsRequest(conversationId, fetchedSuggestions));
    }, [conversation, conversationContext, conversationId, currentLanguage, dispatch, selectedDocument, supportedTags]);

    useEffect(() => {
        if (!conversation || !showSuggestions || initialSuggestionsRequestedRef.current) {
            return;
        }

        const hasUserMessages = conversation.conversationMessages.some((message) => message.role === Role.USER);
        const shouldFetchInitialSuggestions = !hasUserMessages && conversation.suggestions.length === 0;

        if (!shouldFetchInitialSuggestions) {
            return;
        }

        initialSuggestionsRequestedRef.current = true;
        void fetchSuggestionsAndUpdate();
    }, [conversation, fetchSuggestionsAndUpdate, showSuggestions]);

    if (!selectedDocument || !conversation) {
        return null;
    }

    const fetchResponseAndUpdateConversation = async (
        userMessage: string,
        contextOverride: AiAssistantConversationContext = conversationContext,
    ) => {
        if (!userMessage?.trim() || !conversation) {
            return;
        }
        dispatch(doUpdateConversationContextRequest(conversationId, contextOverride));
        dispatch(
            doAddMessageRequest(
                conversationId,
                {
                    text: userMessage,
                    role: Role.USER,
                    metadata: {context: contextOverride},
                },
                activeSearchRequest,
            ),
        );

        const singleDocumentRequest = {...activeSearchRequest, filters: {...activeSearchRequest.filters}};
        singleDocumentRequest.filters['id'] = [new HibuEqualsFilter(selectedDocument.id)];

        const request = getHibuAssistantRequestWithSearchRequest(
            singleDocumentRequest,
            currentLanguage,
            userMessage,
            conversation,
            contextOverride,
        );
        return stream.generate(request, {
            onComplete: message => dispatch(doAddMessageRequest(conversationId, message, activeSearchRequest)),
            onError: () => dispatch(showError(SearchPageType.BASIC, 'assistant.error.responseError')),
        });
    };

    const handleDelete = async () => {
        stream.cancel();
        dispatch(doDeleteConversationRequest(conversationId));
    };

    return (
        <div className="hibu-assistant">
            <ChatSummary/>
            <div className="separator"></div>
            <div className="hibu-assistant-scrollable">
                <ChatWindow
                    ref={stream.chatWindowRef}
                    streamedAnswer={stream.text}
                    conversation={conversation}
                    isGeneratingAnswer={stream.isGeneratingAnswer}
                    suggestionsEnabled={true}
                    updateConversationForSuggestions={fetchResponseAndUpdateConversation}
                    renderSources={false}
                />
            </div>
            <ChatInput
                conversationId={conversationId}
                welcomeMessage={t('assistant.input-search-placeholder-single')}
                getAiResponse={fetchResponseAndUpdateConversation}
                getAiSuggestions={fetchSuggestionsAndUpdate}
                handleResetConversation={handleDelete}
            />
        </div>
    );
};
